import { FC, useRef, useEffect } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { useFormContext } from 'react-hook-form';
import { IMAGE_HEIGHT, IMAGE_WIDTH } from '../BrandCataloguesPage/constants';
import { ICatalogueProductProps } from './types';
import styles from './catalogueProductLayer.module.scss';

export const CatalogueProductLayer: FC<ICatalogueProductProps> = ({ product: { position, id }, imageBoxSize }) => {
  const { getValues, setValue, watch } = useFormContext();
  const dragEl = useRef<HTMLDivElement | null>(null);

  const [{ x, y, width, height }, api] = useSpring(() => ({
    x: IMAGE_WIDTH * (position.left / 100),
    y: IMAGE_HEIGHT * (position.top / 100),
    width: IMAGE_WIDTH * (position.width / 100),
    height: IMAGE_HEIGHT * (position.height / 100),
  }));

  useEffect(() => {
    api.set({
      x: IMAGE_WIDTH * (position.left / 100),
      y: IMAGE_HEIGHT * (position.top / 100),
      width: IMAGE_WIDTH * (position.width / 100),
      height: IMAGE_HEIGHT * (position.height / 100),
    });
  }, [position]);

  const bind = useDrag(
    (state) => {
      const isResizing = state?.event.target === dragEl.current;
      if (isResizing) {
        api.set({
          width: state.offset[0],
          height: state.offset[1],
        });
      } else {
        api.set({
          x: state.offset[0],
          y: state.offset[1],
        });
      }
    },
    {
      from: (event) => {
        const isResizing = event.target === dragEl.current;
        if (isResizing) {
          return [width.get(), height.get()];
        } else {
          return [x.get(), y.get()];
        }
      },
      bounds: (state) => {
        const isResizing = state?.event.target === dragEl.current;
        // const containerWidth = containerRef.current?.clientWidth ?? 0;
        const containerWidth = imageBoxSize.width;
        const containerHeight = imageBoxSize.height;
        if (isResizing) {
          return {
            top: 50,
            left: 50,
            right: containerWidth - x.get(),
            bottom: containerHeight - y.get(),
          };
        } else {
          return {
            top: 0,
            left: 0,
            right: containerWidth - width.get(),
            bottom: containerHeight - height.get(),
          };
        }
      },
    }
  );

  const onMouseUp = () => {
    // convert to persentage
    const position = {
      left: (x.get() / imageBoxSize.width) * 100,
      top: (y.get() / imageBoxSize.height) * 100,
      width: (width.get() / imageBoxSize.width) * 100,
      height: (height.get() / imageBoxSize.height) * 100,
    };
    const prods = [...getValues('products')];
    if (prods) {
      const index = prods.findIndex((item) => item.id === id);
      if (index !== -1) {
        prods[index].position = position;
        setValue('products', prods);
      }
    }
  };

  return (
    <animated.div
      onMouseDown={() => setValue('selectedProdId', id)}
      onMouseUp={onMouseUp}
      className={`${styles.productLayer} ${watch('selectedProdId') === id && styles.selected}`}
      style={{ x, y, width, height }}
      {...bind()}>
      <div className={styles.resizer} ref={dragEl}></div>
    </animated.div>
  );
};
